import sharedSupabase from '../config/supabase';
import { computeNormalizedUnitCost, listAllocatableOptions } from '../utils/purchaseOptions';
import { requestComparisonRecompute } from './comparisonRecomputeService';

/**
 * Purchase Option Service (web)
 *
 * Data access for public.purchase_options — the multi-vendor "ways to buy one
 * item" table. Every function takes an optional trailing `client` (defaults to
 * the shared supabase singleton) so tests can inject a fake.
 *
 * Write helpers here only ever touch purchase_options (and read price_history);
 * deactivating an option must never cascade into inventory_items or templates.
 */

const OPTION_COLUMNS =
  'id, store_id, inventory_item_id, vendor_id, vendor_name_text, vendor_sku, vendor_description, ' +
  'purchase_unit, pack_description, qty_per_package, package_price, normalized_unit_cost, ' +
  'moq_packages, source_type, source_ref, effective_date, verification_status, ' +
  'conversion_status, conversion_snapshot, conversion_provenance, ' +
  'is_preferred, is_approved, is_hidden_from_allocation, status, created_at, updated_at';

// Columns added by later migrations (20260730120000, 20260731120000,
// 20260801120000). The count tool needs them — `last_operational_source_at`
// decides which package is the default count format, and the inner-pack
// columns let the browser build a package ladder without the edge function —
// but a database that hasn't applied those migrations must not lose the
// ENTIRE option fetch over them. Selected optimistically, dropped on error.
const RECENT_OPTION_COLUMNS =
  'last_operational_source_at, last_operational_source_type, ' +
  'inner_pack_count, inner_pack_unit, inner_pack_size, inner_pack_size_unit, ' +
  'allows_fractional_packages, order_increment, ' +
  // The vendor's own lifecycle state rides along so the ordering gates
  // (vendorIsRetired) can exclude a retired vendor's products from every
  // NEW-order path while the packages stay countable.
  'vendor:vendors(display_name, status, is_active, deleted_at)';

// PostgREST/Postgres "column not deployed yet" signatures.
const isMissingColumnError = (error) => {
  const code = String(error?.code || '');
  const message = String(error?.message || '');
  return ['PGRST204', 'PGRST205', '42703'].includes(code)
    || /column .* does not exist|schema cache/i.test(message);
};

/**
 * Select with the full column set, falling back to the legacy set when the
 * newer columns are missing. Options fetched through the fallback simply lack
 * the recency/inner-pack fields, which every consumer treats as "unknown".
 */
async function selectOptions(client, applyFilters) {
  const full = await applyFilters(
    client.from('purchase_options').select(`${OPTION_COLUMNS}, ${RECENT_OPTION_COLUMNS}`),
  );
  if (!full.error) return full;
  if (!isMissingColumnError(full.error)) return full;
  const legacyWithVendorName = await applyFilters(
    client.from('purchase_options').select(`${OPTION_COLUMNS}, vendor:vendors(display_name)`),
  );
  if (!legacyWithVendorName.error) return legacyWithVendorName;
  if (!isMissingColumnError(legacyWithVendorName.error)) return legacyWithVendorName;
  console.warn(
    '[purchase-option] newer option columns unavailable (migration not deployed?):',
    full.error.message,
  );
  return applyFilters(client.from('purchase_options').select(OPTION_COLUMNS));
}

const throwIfError = (error) => {
  if (error) throw new Error(error.message);
};

/**
 * Batch-fetch options for many items, grouped by inventory_item_id.
 * Returns a plain object: { [inventory_item_id]: [option, ...] }.
 */
export async function fetchOptionsForItems(storeId, inventoryItemIds, client = sharedSupabase) {
  if (!storeId || !Array.isArray(inventoryItemIds) || inventoryItemIds.length === 0) return {};
  const { data, error } = await selectOptions(client, (query) =>
    query.eq('store_id', storeId).in('inventory_item_id', inventoryItemIds),
  );
  throwIfError(error);

  const grouped = {};
  (data || []).forEach((option) => {
    const key = option.inventory_item_id;
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(option);
  });
  return grouped;
}

const COMPARISON_ITEM_COLUMNS =
  'id, description, vendor, unit, pack_size, category, is_active, deleted_at, '
  + 'comparison_unit, comparison_unit_source, '
  + 'comparison_unit_confidence, comparison_unit_family, comparison_unit_reasoning, '
  // Item replenishment rules (count unit): buildPurchasePlan and
  // buildAllocationLine shape the purchase target from these.
  + 'replenishment_minimum_qty, replenishment_increment_qty';

/**
 * When each package was last actually BOUGHT — the newest purchase order that
 * was sent (purchase_orders.sent_at) and carried that option.
 *
 * This is the evidence behind the Related Products "Last Used" badge. It is
 * deliberately NOT purchase_options.is_preferred: that column is per-item,
 * has no uniqueness constraint, and says nothing about what anyone bought —
 * which is how a three-vendor group came to show three "Preferred" pills.
 *
 * A draft or unsent order proves nothing and is excluded. Degrades to {} on
 * any error: the badge is an enrichment, never a reason to fail the tab.
 *
 * @returns {Promise<{[purchaseOptionId: string]: string}>} ISO date per option
 */
/**
 * The newest COMPLETED SHOPPING TRIP that actually bought each option.
 *
 * The pickup half of "Last used". A store that buys from Restaurant Depot and
 * Costco never sends a purchase order for those vendors — it takes a shopping
 * list and comes back. Reading only purchase_orders makes the badge vanish on
 * exactly the vendors such a store buys from most, and an absent badge reads
 * as "never bought", which is a worse lie than showing nothing at all.
 *
 * Evidence, deliberately narrow:
 *   - the trip is 'completed' (an open list is a plan, not a purchase), and
 *   - the line was actually found, or a substitute was bought in its place —
 *     in which case the SUBSTITUTE is what got bought, so that is the option
 *     credited (substitute_purchase_option_id), not the one asked for.
 *
 * Degrades to {} on any error, exactly like its purchase-order twin: the badge
 * is an enrichment, never a reason to fail the screen.
 *
 * @returns {Promise<{[purchaseOptionId: string]: string}>} ISO date per option
 */
export async function fetchLastPickupUseByOption(storeId, optionIds, client = sharedSupabase) {
  const ids = (Array.isArray(optionIds) ? optionIds : []).filter((id) => id != null).map(String);
  if (!storeId || ids.length === 0) return {};
  const map = {};
  const CHUNK = 300;
  const record = (optionId, at) => {
    if (!optionId || !at) return;
    const key = String(optionId);
    if (!map[key] || Date.parse(at) > Date.parse(map[key])) map[key] = at;
  };
  // Two passes, because PostgREST cannot filter on coalesce(): the requested
  // option, then the substitute that replaced it.
  const COLUMNS = ['purchase_option_id', 'substitute_purchase_option_id'];
  try {
    for (const column of COLUMNS) {
      for (let start = 0; start < ids.length; start += CHUNK) {
        // eslint-disable-next-line no-await-in-loop
        const { data, error } = await client
          .from('shopping_list_items')
          .select(`${column}, found_status, shopping_lists!inner(completed_at, status, store_id)`)
          .in(column, ids.slice(start, start + CHUNK))
          .eq('shopping_lists.store_id', storeId)
          .eq('shopping_lists.status', 'completed')
          .eq('found_status', 'found');
        if (error) {
          console.warn('[purchase-option] pickup usage unavailable (non-fatal):', error.message);
          return map;
        }
        (data || []).forEach((row) => {
          record(row?.[column], row?.shopping_lists?.completed_at ?? null);
        });
      }
    }
    return map;
  } catch (error) {
    console.warn('[purchase-option] pickup usage lookup failed (non-fatal):', error?.message);
    return map;
  }
}

/**
 * Both halves of "last used", merged: whichever way this store actually bought
 * the option most recently wins.
 */
export async function fetchLastPurchaseUseByOption(storeId, optionIds, client = sharedSupabase) {
  const [ordered, pickedUp] = await Promise.all([
    fetchLastOrderUseByOption(storeId, optionIds, client),
    fetchLastPickupUseByOption(storeId, optionIds, client),
  ]);
  const merged = { ...ordered };
  Object.entries(pickedUp).forEach(([optionId, at]) => {
    if (!merged[optionId] || Date.parse(at) > Date.parse(merged[optionId])) merged[optionId] = at;
  });
  return merged;
}

export async function fetchLastOrderUseByOption(storeId, optionIds, client = sharedSupabase) {
  const ids = (Array.isArray(optionIds) ? optionIds : []).filter((id) => id != null).map(String);
  if (!storeId || ids.length === 0) return {};
  const map = {};
  const CHUNK = 300; // same ceiling fetchOptionsForItems uses
  try {
    for (let start = 0; start < ids.length; start += CHUNK) {
      // Sequential on purpose: a catalog-wide sweep should not open dozens of
      // parallel connections behind the user's first paint.
      // eslint-disable-next-line no-await-in-loop
      const { data, error } = await client
        .from('purchase_order_items')
        .select('purchase_option_id, purchase_orders!inner(sent_at, store_id)')
        .in('purchase_option_id', ids.slice(start, start + CHUNK))
        .eq('purchase_orders.store_id', storeId)
        .not('purchase_orders.sent_at', 'is', null);
      if (error) {
        console.warn('[purchase-option] order usage unavailable (non-fatal):', error.message);
        return map;
      }
      (data || []).forEach((row) => {
        const optionId = row?.purchase_option_id != null ? String(row.purchase_option_id) : null;
        const sentAt = row?.purchase_orders?.sent_at ?? null;
        if (!optionId || !sentAt) return;
        if (!map[optionId] || Date.parse(sentAt) > Date.parse(map[optionId])) map[optionId] = sentAt;
      });
    }
    return map;
  } catch (error) {
    console.warn('[purchase-option] order usage lookup failed (non-fatal):', error?.message);
    return map;
  }
}

/**
 * The inventory rows behind a set of options, carrying the comparison facts
 * (`comparison_unit` and how it was decided) that make two vendors' prices
 * comparable. Options alone cannot answer "cheaper per what?" — the unit lives
 * on the item. The item's own `unit` rides along for the package-basis
 * validator, and its liveness columns (`is_active`, `deleted_at`) let the
 * Savings Review keep archived items out of worksheets and orders.
 *
 * Returns { [id]: item }. A store behind on the comparison migrations degrades
 * to the pre-comparison columns rather than failing: every line then reads
 * "not price-compared yet", which is the honest answer.
 */
export async function fetchComparisonItems(storeId, inventoryItemIds, client = sharedSupabase) {
  if (!storeId || !Array.isArray(inventoryItemIds) || inventoryItemIds.length === 0) return {};
  const run = (columns) => client
    .from('inventory_items')
    .select(columns)
    .eq('store_id', storeId)
    .in('id', inventoryItemIds);

  let { data, error } = await run(COMPARISON_ITEM_COLUMNS);
  if (error && isMissingColumnError(error)) {
    console.warn(
      '[purchase-option] comparison columns unavailable (migration not deployed?):',
      error.message,
    );
    ({ data, error } = await run('id, description, vendor, unit, pack_size, category, is_active, deleted_at'));
  }
  if (error) return {};

  const map = {};
  (data || []).forEach((row) => { map[row.id] = row; });
  return map;
}

/**
 * vendor_id → display_name for a store, so an option that IS linked to a
 * vendor renders that vendor's canonical name rather than the raw string its
 * price list happened to use.
 */
export async function fetchVendorNames(storeId, client = sharedSupabase) {
  if (!storeId) return {};
  const { data, error } = await client
    .from('vendors')
    .select('id, display_name')
    .eq('store_id', storeId);
  if (error) return {};

  const map = {};
  (data || []).forEach((row) => { map[row.id] = row.display_name; });
  return map;
}

/**
 * Allocation-eligible options for one item, sorted preferred-first then
 * cheapest normalized_unit_cost (see listAllocatableOptions).
 */
export async function fetchOptionsForItem(storeId, inventoryItemId, client = sharedSupabase) {
  if (!storeId || !inventoryItemId) return [];
  const { data, error } = await selectOptions(client, (query) =>
    query.eq('store_id', storeId).eq('inventory_item_id', inventoryItemId),
  );
  throwIfError(error);
  return listAllocatableOptions(data || []);
}

/**
 * Flags `optionId` as the preferred way to buy its item, clearing the flag on
 * the item's other options. Clearing is best-effort (non-fatal) — the target
 * flag is the operation that matters to the caller.
 */
export async function makePreferred(optionId, inventoryItemId, client = sharedSupabase) {
  if (!optionId) throw new Error('optionId is required');
  const { error } = await client
    .from('purchase_options')
    .update({ is_preferred: true })
    .eq('id', optionId);
  throwIfError(error);

  if (!inventoryItemId) return;
  try {
    const { error: clearError } = await client
      .from('purchase_options')
      .update({ is_preferred: false })
      .eq('inventory_item_id', inventoryItemId)
      .neq('id', optionId);
    if (clearError) {
      console.warn('[purchase-option] Clearing other preferred flags failed:', clearError.message);
    }
  } catch (err) {
    console.warn('[purchase-option] Clearing other preferred flags failed:', err?.message);
  }
}

// After a write changes what the relationship contains (or who may buy from
// it), ask the server to re-resolve the relationship comparison. Best-effort
// and fire-and-forget: the write itself already succeeded.
async function recomputeComparisonForOption(client, optionId) {
  try {
    const { data } = await client
      .from('purchase_options')
      .select('store_id, inventory_item_id')
      .eq('id', optionId)
      .maybeSingle();
    if (data?.store_id && data?.inventory_item_id != null) {
      void requestComparisonRecompute(data.store_id, data.inventory_item_id, client);
    }
  } catch {
    // Comparison freshness is a nicety; the write stands either way.
  }
}

export async function setApproved(optionId, isApproved, client = sharedSupabase) {
  if (!optionId) throw new Error('optionId is required');
  const { error } = await client
    .from('purchase_options')
    .update({ is_approved: !!isApproved })
    .eq('id', optionId);
  throwIfError(error);
  void recomputeComparisonForOption(client, optionId);
}

/**
 * Updates ONE vendor product's ordering rules — purchase_options.moq_packages,
 * order_increment, allows_fractional_packages. This is the single write path
 * every "Edit ordering rules" surface uses (Related Products, Savings Review,
 * draft PO review): there is one stored copy of a vendor minimum, on the
 * option row, and this edits it.
 *
 * No client-side recalculation follows: the DB's order-facts token trigger
 * (purchase_options_refresh_open_allocations) re-prices every open, un-fanned
 * allocation line selecting this option in the same transaction, so callers
 * just re-read. Comparison caches deliberately exclude these fields, so no
 * comparison recompute is requested either.
 *
 * @param {*} optionId
 * @param {{ moqPackages?: number|null, orderIncrement?: number|null,
 *           allowsFractional?: boolean }} rules  undefined fields are left
 *          untouched; a missing/non-positive MOQ becomes the operational
 *          default of one purchase unit, while null clears an order increment.
 * @returns {Promise<Object>} the updated purchase_options row
 */
export async function updateOrderingRules(optionId, rules = {}, client = sharedSupabase) {
  if (!optionId) throw new Error('optionId is required');
  const positiveOrNull = (value) => {
    if (value === null) return null;
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? n : null;
  };
  const patch = {};
  if ('moqPackages' in rules) patch.moq_packages = positiveOrNull(rules.moqPackages) ?? 1;
  if ('orderIncrement' in rules) patch.order_increment = positiveOrNull(rules.orderIncrement);
  if ('allowsFractional' in rules) patch.allows_fractional_packages = rules.allowsFractional === true;
  if (Object.keys(patch).length === 0) throw new Error('No ordering rules to update');

  const run = (body) => client
    .from('purchase_options')
    .update(body)
    .eq('id', optionId)
    .select()
    .single();

  let { data, error } = await run(patch);
  // order_increment (20260807120000) and allows_fractional_packages
  // (20260730120000) are late columns. A store behind on them must still be
  // able to save the vendor minimum — retry with the stripped patch when the
  // stripped values were defaults; refuse loudly when the user actually
  // typed a rule the database cannot hold, so nothing is silently discarded.
  if (error && isMissingColumnError(error)) {
    const stripped = { ...patch };
    const droppedTypedRule =
      (stripped.order_increment !== undefined && stripped.order_increment !== null)
      || stripped.allows_fractional_packages === true;
    delete stripped.order_increment;
    delete stripped.allows_fractional_packages;
    if (droppedTypedRule) {
      throw new Error(
        "This store's database does not support order increments or fractional ordering yet — apply the pending migrations, or save only the vendor minimum.",
      );
    }
    if (Object.keys(stripped).length === 0) return null;
    console.warn(
      '[purchase-option] ordering-rule columns unavailable (migration not deployed?):',
      error.message,
    );
    ({ data, error } = await run(stripped));
  }
  throwIfError(error);
  return data;
}

export async function setHiddenFromAllocation(optionId, isHidden, client = sharedSupabase) {
  if (!optionId) throw new Error('optionId is required');
  const { error } = await client
    .from('purchase_options')
    .update({ is_hidden_from_allocation: !!isHidden })
    .eq('id', optionId);
  throwIfError(error);
  void recomputeComparisonForOption(client, optionId);
}

/**
 * Soft-delete: flips status to 'inactive'. Touches purchase_options ONLY —
 * never inventory_items, count templates, or any other table.
 */
export async function deactivate(optionId, client = sharedSupabase) {
  if (!optionId) throw new Error('optionId is required');
  const { error } = await client
    .from('purchase_options')
    .update({ status: 'inactive' })
    .eq('id', optionId);
  throwIfError(error);
  void recomputeComparisonForOption(client, optionId);
}

export async function reactivate(optionId, client = sharedSupabase) {
  if (!optionId) throw new Error('optionId is required');
  const { error } = await client
    .from('purchase_options')
    .update({ status: 'active' })
    .eq('id', optionId);
  throwIfError(error);
  void recomputeComparisonForOption(client, optionId);
}

// Columns a manual upsert may set. Everything else (snapshots, provenance,
// status) is owned by other flows and left alone on update.
const MANUAL_FIELDS = [
  'vendor_id',
  'vendor_name_text',
  'vendor_sku',
  'vendor_description',
  'purchase_unit',
  'pack_description',
  'qty_per_package',
  'package_price',
  'moq_packages',
  'order_increment',
  'allows_fractional_packages',
];

// Dedupe identity mirrors the purchase_options_dedupe_key unique index:
// (inventory_item_id, coalesce(vendor_id,''), coalesce(vendor_sku,''),
//  coalesce(purchase_unit,''), coalesce(pack_description,'')).
const dedupeKeyOf = (row) =>
  [row?.vendor_id, row?.vendor_sku, row?.purchase_unit, row?.pack_description]
    .map((value) => (value === null || value === undefined ? '' : String(value)))
    .join('|');

// A write that carries the operational-recency stamp, retried without it when
// the columns are not deployed. The stamp is metadata about the write, never
// worth failing the write over.
async function writeOptionWithStamp(client, run, payload, stamp) {
  const stamped = stamp ? { ...payload, ...stamp } : payload;
  const first = await run(stamped);
  if (!first.error || !stamp || !isMissingColumnError(first.error)) return first;
  console.warn(
    '[purchase-option] operational recency columns unavailable (migration not deployed?):',
    first.error.message,
  );
  return run(payload);
}

/**
 * Insert a manual purchase option, or update the existing row that shares its
 * dedupe identity (item + vendor + sku + purchase unit + pack). Computes
 * normalized_unit_cost from package_price / qty_per_package when possible.
 *
 * Real events also stamp `last_operational_source_at` — the recency signal the
 * count tool's default-package precedence reads. An insert is always a real
 * event; an update is one only when it changes the package's CONTENTS
 * (qty_per_package): the dedupe identity guarantees vendor/SKU/unit/pack are
 * unchanged, so everything else is a price refresh and must not move the
 * user's default count format.
 *
 * @param {number|string} storeId
 * @param {number|string} inventoryItemId
 * @param {Object} fields - any of MANUAL_FIELDS plus optional effective_date,
 *                          verification_status, is_approved, is_preferred
 * @param {Object} [opts] - { operationalSource } one of the
 *                          purchase_options_operational_source CHECK values;
 *                          defaults to 'item_edit'.
 * @returns {Promise<Object>} the inserted/updated purchase_options row
 */
export async function upsertManualOption(storeId, inventoryItemId, fields = {}, optsOrClient = {}, maybeClient = undefined) {
  // Back-compat: the 4th arg used to be the client. A supabase client has
  // .from; an options bag does not.
  const optsIsClient = typeof optsOrClient?.from === 'function';
  const opts = optsIsClient ? {} : (optsOrClient || {});
  const client = optsIsClient ? optsOrClient : (maybeClient || sharedSupabase);
  if (!storeId) throw new Error('storeId is required');
  if (!inventoryItemId) throw new Error('inventoryItemId is required');

  const normalizedCost =
    computeNormalizedUnitCost(fields) ?? fields.normalized_unit_cost ?? null;

  const payload = { normalized_unit_cost: normalizedCost };
  MANUAL_FIELDS.forEach((key) => {
    if (key in fields) payload[key] = fields[key];
  });
  // purchase_options.moq_packages is NOT NULL DEFAULT 1. Callers that expose
  // an optional-looking field must not defeat that creation default by
  // explicitly sending null (or later fail when clearing the field).
  if ('moq_packages' in fields) {
    const statedMoq = Number(fields.moq_packages);
    payload.moq_packages = Number.isFinite(statedMoq) && statedMoq > 0 ? statedMoq : 1;
  }
  // Whether the package size is known. A caller that cannot state how many
  // count units a package holds must be able to say so — writing a guess is
  // how a case gets priced as a gallon.
  if ('conversion_status' in fields) payload.conversion_status = fields.conversion_status;
  if ('effective_date' in fields) payload.effective_date = fields.effective_date;
  if ('verification_status' in fields) payload.verification_status = fields.verification_status;
  if ('is_approved' in fields) payload.is_approved = fields.is_approved;
  if ('is_preferred' in fields) payload.is_preferred = fields.is_preferred;

  const { data: existing, error: lookupError } = await client
    .from('purchase_options')
    .select('id, vendor_id, vendor_sku, purchase_unit, pack_description, qty_per_package')
    .eq('store_id', storeId)
    .eq('inventory_item_id', inventoryItemId);
  throwIfError(lookupError);

  const identity = dedupeKeyOf(fields);
  const match = (existing || []).find((row) => dedupeKeyOf(row) === identity);

  // `operationalSource: null` opts out entirely — for backfill-like writes
  // (the legacy-pricing mirror heal) that reconstruct history rather than
  // record an event, and must not outrank genuine ones.
  const sourceType = opts.operationalSource === null ? null : (opts.operationalSource || 'item_edit');
  const stamp = sourceType
    ? {
        last_operational_source_at: new Date().toISOString(),
        last_operational_source_type: sourceType,
      }
    : null;

  if (match) {
    const qtyChanged =
      stamp !== null &&
      'qty_per_package' in payload &&
      Number(payload.qty_per_package) !== Number(match.qty_per_package);
    const { data, error } = await writeOptionWithStamp(
      client,
      (body) => client.from('purchase_options').update(body).eq('id', match.id).select().single(),
      payload,
      qtyChanged ? stamp : null,
    );
    throwIfError(error);
    void requestComparisonRecompute(storeId, inventoryItemId, client);
    return data;
  }

  const { data, error } = await writeOptionWithStamp(
    client,
    (body) => client.from('purchase_options').insert(body).select().single(),
    {
      store_id: storeId,
      inventory_item_id: inventoryItemId,
      source_type: 'manual',
      ...payload,
    },
    stamp,
  );
  throwIfError(error);
  void requestComparisonRecompute(storeId, inventoryItemId, client);
  return data;
}

/**
 * Item ids (from the given list) that have at least two allocatable options —
 * i.e. active + approved + not hidden from allocation. Used to badge items
 * where the buyer has a real vendor choice.
 *
 * @returns {Promise<Set>} Set of inventory_item_id values (numeric keys when
 *                          the ids came in as numbers).
 */
export async function fetchFlexibleSourceItemIds(storeId, inventoryItemIds, client = sharedSupabase) {
  if (!storeId || !Array.isArray(inventoryItemIds) || inventoryItemIds.length === 0) {
    return new Set();
  }
  const run = (columns) => client
    .from('purchase_options')
    .select(columns)
    .eq('store_id', storeId)
    .in('inventory_item_id', inventoryItemIds)
    .eq('status', 'active')
    .eq('is_approved', true)
    .eq('is_hidden_from_allocation', false);
  // The vendor embed rides along so a retired vendor's options stop counting
  // toward "flexible source" — an item whose second vendor was retired is a
  // fixed-source item again. Falls back to the plain select if the embed is
  // unavailable, which simply counts every option as before.
  let { data, error } = await run('inventory_item_id, vendor:vendors(display_name, status, is_active, deleted_at)');
  if (error) {
    ({ data, error } = await run('inventory_item_id'));
  }
  throwIfError(error);

  const counts = new Map();
  (data || []).forEach((row) => {
    if (vendorIsRetired(row)) return;
    counts.set(row.inventory_item_id, (counts.get(row.inventory_item_id) || 0) + 1);
  });
  const flexible = new Set();
  counts.forEach((count, itemId) => {
    if (count >= 2) flexible.add(itemId);
  });
  return flexible;
}

/**
 * Price-history ledger rows for one item, newest first. Rows may carry
 * purchase_option_id / normalized_unit_cost / effective_date (migration
 * 20260724120005) linking a price change back to the option it came from.
 */
export async function fetchOptionPriceHistory(storeId, inventoryItemId, client = sharedSupabase) {
  if (!storeId || !inventoryItemId) return [];
  const { data, error } = await client
    .from('price_history')
    .select('*')
    .eq('store_id', storeId)
    .eq('inventory_item_id', inventoryItemId)
    .order('changed_at', { ascending: false })
    .limit(200);
  throwIfError(error);
  return data || [];
}
